import React from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import Modal from "../common/Modal";
import { useCreativeUsage } from "../../hooks/useCreativeUsage";

interface DeleteCreativesModalProps {
  /** The creatives queued for deletion; null when nothing is pending. */
  pending: { ids: string[]; names: string[] } | null;
  onCancel: () => void;
  onConfirm: (ids: string[]) => void;
  isDeleting?: boolean;
}

/**
 * Confirmation for deleting creatives.
 *
 * Deleting a creative removes it from every playlist that holds it — that is
 * the point of the feature (retiring a festival ad used to mean unpicking it
 * from twenty playlists by hand), but it is also not reversible from the
 * portal: the creative can be re-uploaded, its place in those playlists
 * cannot. So the reach is fetched and shown before the operator commits,
 * rather than after.
 */
const DeleteCreativesModal: React.FC<DeleteCreativesModalProps> = ({
  pending,
  onCancel,
  onConfirm,
  isDeleting = false,
}) => {
  const { usage, isLoading } = useCreativeUsage(pending?.ids ?? null);

  if (!pending) return null;

  const { ids, names } = pending;
  const { playlistCount, screenCount, playlists } = usage;
  const subject =
    names.length === 1 ? names[0] : `${ids.length} creatives`;

  return (
    <Modal
      isOpen
      onClose={onCancel}
      title={ids.length === 1 ? "Delete Creative" : "Delete Creatives"}
      size="lg"
    >
      <div>
        <p className="text-sm text-gray-700 mb-4">
          Delete <span className="font-semibold">{subject}</span>?
        </p>

        {isLoading ? (
          <div className="p-4 mb-4 text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg">
            Checking where {ids.length === 1 ? "it is" : "they are"} used…
          </div>
        ) : playlistCount > 0 ? (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
            <ExclamationTriangleIcon className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-700">
              <p className="font-medium text-gray-800 mb-1">
                This also removes {ids.length === 1 ? "it" : "them"} from{" "}
                {playlistCount} playlist{playlistCount === 1 ? "" : "s"}
                {screenCount > 0 && (
                  <>
                    , changing what {screenCount} screen
                    {screenCount === 1 ? "" : "s"} play
                    {screenCount === 1 ? "s" : ""}
                  </>
                )}
                .
              </p>
              <p>
                The playlists themselves are kept, and their durations are
                recalculated. Putting the creative back means adding it to each
                playlist again by hand.
              </p>

              <ul className="mt-3 max-h-40 overflow-y-auto space-y-1">
                {playlists.map((playlist) => (
                  <li
                    key={playlist.id}
                    className="flex items-center justify-between gap-3 text-xs bg-white border border-amber-200 rounded px-2 py-1"
                  >
                    <span className="truncate">{playlist.name}</span>
                    {playlist.type && (
                      <span className="text-gray-500 capitalize flex-shrink-0">
                        {playlist.type}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <div className="p-4 mb-4 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg">
            {ids.length === 1 ? "It is" : "They are"} not used in any playlist.
          </div>
        )}

        <p className="text-xs text-gray-400 mb-4">
          Play history is kept — reports already generated for{" "}
          {ids.length === 1 ? "this creative" : "these creatives"} stay
          available.
        </p>

        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(ids)}
            disabled={isDeleting || isLoading}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg"
          >
            {isDeleting
              ? "Deleting…"
              : playlistCount > 0
                ? `Delete and remove from ${playlistCount} playlist${
                    playlistCount === 1 ? "" : "s"
                  }`
                : "Delete"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteCreativesModal;
